/** Pure card construction for the Home (attention) page — one card per
 *  non-zero count / true staleness flag, most urgent first (UX spec §3.8).
 *  Copy register: plain distribution-ops English (spec §1) — no enums, no
 *  script paths, no GUIDs. */

import type { AttentionSummary } from '../../types/attention.ts'

export interface AttentionCard {
  key: string
  headline: string
  detail: string
  to: string
  linkLabel: string
}

function count(n: number, singular: string, plural: string): string {
  return `${n.toLocaleString()} ${n === 1 ? singular : plural}`
}

export function buildAttentionCards(
  s: AttentionSummary,
  canSeeGisQueue: boolean,
): AttentionCard[] {
  const cards: AttentionCard[] = []

  if (s.active_outages > 0) {
    cards.push({
      key: 'active-outages',
      headline: count(s.active_outages, 'active outage', 'active outages'),
      detail: 'Live events from the meter feed.',
      to: '/outages',
      linkLabel: 'Open the outage board',
    })
  }
  if (s.stale_oms) {
    cards.push({
      key: 'stale-oms',
      headline: 'Outage feed is behind',
      detail:
        'The meter outage feed has not updated recently, so the outage board may be out of date.',
      to: '/outages',
      linkLabel: 'Check the outage board',
    })
  }
  if (s.overloaded_transformers > 0) {
    cards.push({
      key: 'overloaded',
      headline: count(
        s.overloaded_transformers,
        'overloaded transformer',
        'overloaded transformers',
      ),
      detail: 'Running above their nameplate rating on measured load.',
      // ?status= seeds the loading table's status filter (engineeringTab.ts),
      // so the card lands on exactly the rows it counted.
      to: '/engineering?status=overloaded',
      linkLabel: 'Open capacity',
    })
  }
  if (s.divergent_transformers > 0) {
    cards.push({
      key: 'divergent',
      headline: count(
        s.divergent_transformers,
        'transformer where model and meters disagree',
        'transformers where model and meters disagree',
      ),
      detail:
        'Usually a wrong kVA rating, wrong meter links, or a dead meter. Worth a look.',
      // 'divergent' becomes a real filter option in Task 8.2 (A7); until then
      // the param is simply ignored by the table and the card lands unfiltered.
      to: '/engineering?status=divergent',
      linkLabel: 'Open capacity',
    })
  }
  if (s.heavy_transformers > 0) {
    cards.push({
      key: 'heavy',
      headline: count(
        s.heavy_transformers,
        'heavily loaded transformer',
        'heavily loaded transformers',
      ),
      detail:
        'Above 70% of nameplate on measured load — a watch list, not an emergency.',
      to: '/engineering?status=heavy',
      linkLabel: 'Open capacity',
    })
  }
  // Voltage content only renders inside the feeder detail tab, which needs a
  // selected feeder — so this card links to the WORST feeder by name of id
  // (?tab=feeder&feeder=<id>, read once by the dashboard). A violating count
  // always yields a worst feeder; the null guard is defensive.
  if (s.feeders_with_violations > 0 && s.worst_voltage_feeder_id != null) {
    cards.push({
      key: 'voltage',
      headline: count(
        s.feeders_with_violations,
        'feeder with low-voltage areas',
        'feeders with low-voltage areas',
      ),
      detail:
        'Calculated voltage drop exceeds the limit somewhere on these feeders. Opens the worst one.',
      to: `/engineering?tab=feeder&feeder=${s.worst_voltage_feeder_id}`,
      linkLabel: 'Open voltage drop',
    })
  }
  if (s.stale_ami) {
    cards.push({
      key: 'stale-ami',
      headline: 'Meter data is behind',
      detail:
        'The nightly meter-data load has not run recently, so measured loading may be out of date.',
      to: '/engineering?tab=ami',
      linkLabel: 'Check meter data status',
    })
  }
  if (s.pending_discrepancies > 0) {
    cards.push({
      key: 'discrepancies',
      headline: count(
        s.pending_discrepancies,
        'map difference waiting for review',
        'map differences waiting for review',
      ),
      detail: 'Places where the GIS map and this system disagree.',
      to: canSeeGisQueue ? '/gis-queue' : '/discrepancies',
      linkLabel: canSeeGisQueue ? 'Open the GIS queue' : 'Open discrepancies',
    })
  }
  if (s.secondary_review_count > 0) {
    cards.push({
      key: 'secondary-review',
      headline: count(
        s.secondary_review_count,
        'service-wire connection needs a decision',
        'service-wire connections need a decision',
      ),
      detail:
        'The derived low-voltage network could not settle these on its own.',
      to: '/secondary-review',
      linkLabel: 'Open secondary review',
    })
  }
  if (s.unrated_devices > 0) {
    cards.push({
      key: 'unrated-devices',
      headline: count(
        s.unrated_devices,
        'protective device missing a fault rating',
        'protective devices missing a fault rating',
      ),
      detail:
        'Fault studies cannot judge these devices until a rating is on record.',
      to: '/engineering?tab=fault',
      linkLabel: 'Open fault analysis',
    })
  }

  return cards
}
